import { api } from 'api/api';
import { Endpoints } from 'api/constants';
import {
  IInternalShopInventoryEvent,
  IInternalShopInventoryEventsAdditionalInfo,
  IInternalShopInventoryItem,
  IInternalShopItem,
} from 'types/income';
import { Translations } from 'types/Common';
import { exportXlsxConfig, toCents } from 'utils/general';
import { ShopItemCategory } from 'types/MarketplaceItems';
import { ShopItemStatus } from 'store/shop';
import { TimeUnit, TableDataResponse } from 'types/general';
import {
  InternalShopFilters,
  InternalShopInventoryEventsFilters,
  InternalShopInventoryFilters,
  InternalShopItemTranslations,
  PurchaseLimitPeriod,
  PurchaseLimitVariant,
} from './internal-shop.types';

const getInventoryItemsFilters = (
  filters: Partial<InternalShopInventoryFilters>,
) => ({
  page: filters.page,
  pageSize: filters.pageSize,
  filter: {
    itemIds: filters.items,
    inventoryMin: filters.inventory?.from,
    inventoryMax: filters.inventory?.to,
  },
});

interface GetItems {
  filters: Partial<InternalShopFilters>;
}

interface GetItem {
  itemId: string;
}

interface CreateItem {
  name: string;
  categories: ShopItemCategory[];
  status: ShopItemStatus;
  translations: InternalShopItemTranslations;
  imageId: string;
  prices: Array<{
    id: string;
    price: number;
    initialInventory: number;
    index: number;
    translations: Translations<{ title: string }>;
  }>;
  countries: string[];
  trackInventory: boolean;
  purchaseLimit: number;
  purchaseLimitType: PurchaseLimitVariant;
  purchaseLimitPeriod: PurchaseLimitPeriod;
  notifyOnMerchandisingOrder: boolean;
  resolutionDuration: number;
  resolutionDurationUnit: TimeUnit;
  requireShipping: boolean;
}

interface UpdateItem {
  itemId: string;
  data: Partial<CreateItem>;
}

interface GetInventoryItems {
  filters: Partial<InternalShopInventoryFilters>;
}

interface ExportXlsInventoryItems {
  filters: Partial<InternalShopInventoryFilters>;
}

interface UpdateVariantInventory {
  variantId: string;
  data: {
    comment: string;
    inventoryChange: number;
  };
}

interface UpdateLowQuantity {
  variantId: string;
  data: {
    lowQuantity: number;
  };
}

interface ExportXls {
  variantId: string;
}

interface GetInternalShopInventoryEvents {
  variantId: string;
  filters: Partial<InternalShopInventoryEventsFilters>;
}

export const loaders = {
  getItems: ({ filters }: GetItems) => {
    return api<TableDataResponse<IInternalShopItem>>({
      url: Endpoints.clientAdmin.shop.internal.root,
      params: {
        page: filters.page,
        pageSize: filters.pageSize,
        filter: {
          itemName: filters.name || undefined,
        },
      },
    });
  },
  getItem: ({ itemId }: GetItem) => {
    return api<IInternalShopItem>({
      url: Endpoints.clientAdmin.shop.internal.byId.replace(':itemId', itemId),
    });
  },
  createItem: ({ prices, ...data }: CreateItem) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.shop.internal.root,
      method: 'post',
      data: {
        ...data,
        prices: prices?.map((price) => ({
          id: price.id || undefined,
          price: toCents(price.price),
          index: price.index,
          initialInventory: price.id ? undefined : price.initialInventory,
          translations: price.translations,
        })),
      },
    });
  },
  updateItem: ({ itemId, data: { prices, ...data } }: UpdateItem) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.shop.internal.byId.replace(':itemId', itemId),
      method: 'patch',
      data: {
        ...data,
        prices: prices?.map((price) => ({
          id: price.id || undefined,
          price: toCents(price.price),
          index: price.index,
          initialInventory: price.id ? undefined : price.initialInventory,
          translations: price.translations,
        })),
      },
    });
  },
  getInventoryItems: ({ filters }: GetInventoryItems) => {
    return api<TableDataResponse<IInternalShopInventoryItem>>({
      url: Endpoints.clientAdmin.shop.internal.inventory.root,
      params: getInventoryItemsFilters(filters),
    });
  },
  exportXlsInventoryItems: ({ filters }: ExportXlsInventoryItems) => {
    return api({
      url: Endpoints.clientAdmin.shop.internal.inventory.export.xls,
      params: {
        filter: getInventoryItemsFilters(filters).filter,
      },
      ...exportXlsxConfig,
    });
  },
  updateVariantInventory: ({ variantId, data }: UpdateVariantInventory) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.shop.internal.inventory.byId.root.replace(
        ':variantId',
        variantId,
      ),
      data: {
        inventoryChange: data.inventoryChange,
        comment: data.comment,
      },
      method: 'post',
    });
  },
  updateLowQuantity: ({ variantId, data }: UpdateLowQuantity) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.shop.internal.inventory.byId.updateLowQuantity.replace(
        ':variantId',
        variantId,
      ),
      data: {
        lowInventoryAlert: data.lowQuantity,
      },
      method: 'patch',
    });
  },
  getInventoryEvents: ({
    variantId,
    filters,
  }: GetInternalShopInventoryEvents) => {
    return api<
      TableDataResponse<IInternalShopInventoryEvent> &
        IInternalShopInventoryEventsAdditionalInfo
    >({
      url: Endpoints.clientAdmin.shop.internal.inventory.byId.root.replace(
        ':variantId',
        variantId,
      ),
      params: {
        page: filters.page,
        pageSize: filters.pageSize,
      },
    });
  },
  exportXls: ({ variantId }: ExportXls) => {
    return api({
      url: Endpoints.clientAdmin.shop.internal.inventory.byId.export.xls.replace(
        ':variantId',
        variantId,
      ),
      ...exportXlsxConfig,
    });
  },
};
